/**
 * Windowing that does not break `position: sticky`.
 *
 * The usual virtualiser gives the scroll container one tall spacer and absolutely positions each
 * visible row inside it. That is fine until the list has sticky season bands, which it does: an
 * absolutely positioned element has nothing to stick to, so the band scrolls away and the reader
 * loses track of which year he is looking at somewhere around 2019. The brief is explicit that if
 * the windowing drops the headers, the windowing is what gets fixed.
 *
 * So this one keeps every rendered item in NORMAL FLOW and pads with two plain divs — one above
 * the window, one below. Sticky then works exactly as it does in an unvirtualised list, because
 * as far as the browser is concerned that is what this is.
 *
 * Two details that are load-bearing:
 *
 *   THE WINDOW ALWAYS STARTS ON A BAND. If the first visible item is a turn, the start index walks
 *   back to the band above it. Without that, scrolling into the middle of 2016 renders no band at
 *   all and nothing is stuck to the top — the header does not "unstick", it was never there.
 *
 *   HEIGHTS COME FROM THE CSS. They are read off custom properties on the container rather than
 *   written here, so a row that changes height in the stylesheet cannot leave the offsets behind.
 *   They are re-read on resize, because the phone and desktop rows are different heights.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Rows rendered beyond the viewport at each end, so a fast flick does not show blank. */
const OVERSCAN = 8;

export interface Windowed {
  /** Index of the first rendered item — always a band, or 0. */
  start: number;
  /** Index one past the last rendered item. */
  end: number;
  topPad: number;
  bottomPad: number;
  /** Attach to the scrolling element. */
  ref: (el: HTMLElement | null) => void;
}

export interface WindowedOptions<T> {
  items: readonly T[];
  /** The CSS custom property holding this item's height, e.g. `--fm-band-h`. */
  heightVar: (item: T) => string;
  /** True for items the window must start on — the sticky ones. */
  isAnchor: (item: T) => boolean;
  /** Fallback height, used only before the container has been measured. */
  fallback?: number;
}

export function useWindowedList<T>(opts: WindowedOptions<T>): Windowed {
  const { items, heightVar, isAnchor, fallback = 48 } = opts;
  const el = useRef<HTMLElement | null>(null);
  const [scroll, setScroll] = useState(0);
  const [viewport, setViewport] = useState(0);
  /*
   * Resolved pixel values for each custom property, keyed by property name. Kept in state rather
   * than a ref because a change to it must re-render: on a rotate from phone to tablet width every
   * offset in the list moves, and a ref would leave the window measuring the old geometry.
   */
  const [sizes, setSizes] = useState<Record<string, number>>({});

  const measure = useCallback(() => {
    const node = el.current;
    if (!node) return;
    const style = getComputedStyle(node);
    const next: Record<string, number> = {};
    for (const item of items) {
      const name = heightVar(item);
      if (next[name] !== undefined) continue;
      const raw = style.getPropertyValue(name).trim();
      const px = Number.parseFloat(raw);
      next[name] = Number.isFinite(px) && px > 0 ? px : fallback;
    }
    setSizes((prev) => {
      const same = Object.keys(next).length === Object.keys(prev).length
        && Object.entries(next).every(([k, v]) => prev[k] === v);
      return same ? prev : next;
    });
    setViewport(node.clientHeight);
    setScroll(node.scrollTop);
  }, [items, heightVar, fallback]);

  const ref = useCallback((node: HTMLElement | null) => {
    el.current = node;
    if (node) measure();
  }, [measure]);

  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const node = el.current;
    if (!node) return undefined;
    const onScroll = () => setScroll(node.scrollTop);
    node.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    ro?.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      node.removeEventListener('scroll', onScroll);
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  const height = (item: T) => sizes[heightVar(item)] ?? fallback;

  // Prefix offsets. 141 items, recomputed on scroll — cheap enough that memoising it would cost
  // more in invalidation bugs than it saves in arithmetic.
  const offsets: number[] = [];
  let total = 0;
  for (const item of items) { offsets.push(total); total += height(item); }

  if (items.length === 0) return { start: 0, end: 0, topPad: 0, bottomPad: 0, ref };

  // Before the container has been measured the viewport is 0, which would render nothing at all
  // and leave a blank card on the first paint. Render the first screenful instead.
  const view = viewport || 600;

  let start = 0;
  while (start < items.length - 1 && offsets[start + 1]! <= scroll) start += 1;
  start = Math.max(0, start - OVERSCAN);
  // Walk back to the band that governs this window, so there is always something to stick.
  while (start > 0 && !isAnchor(items[start]!)) start -= 1;

  let end = start;
  while (end < items.length && offsets[end]! < scroll + view) end += 1;
  end = Math.min(items.length, end + OVERSCAN);

  const last = end - 1;
  return {
    start,
    end,
    topPad: offsets[start]!,
    bottomPad: Math.max(0, total - (offsets[last]! + height(items[last]!))),
    ref,
  };
}
